import React, { useState } from "react";
import { Maximize2 } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { GlowingEffect } from "../ui/glowing-effect";
import { Modal } from "../ui/Modal";
import { professionalExperience } from "../../Data/experience";
import { useLang } from "../../context/LanguageContext";
import { t } from "../../translations";

export default function Experience() {
    const [openProject, setOpenProject] = useState(null);
    const { lang } = useLang();
    const tr = t[lang].experience;

    return (
        <section id="professional-experience" className="w-full bg-bg transition-colors duration-200">
            <div className="max-w-7xl mx-auto pt-12 sm:pt-16 md:pt-24 px-4 sm:px-8 md:px-16 pb-12 sm:pb-16 md:pb-24">
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-8 text-center text-label">
                    {tr.title}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {professionalExperience.map((exp, idx) => (
                        <Card
                            key={exp.title.en}
                            className="relative bg-surface-elevated border border-separator rounded-3xl shadow-card transition-all duration-300 hover:-translate-y-1 hover:shadow-card-hover cursor-pointer overflow-hidden group"
                            onClick={() => setOpenProject(idx)}
                        >
                            <GlowingEffect
                                spread={70}
                                glow={false}
                                disabled={false}
                                proximity={120}
                                inactiveZone={0.01}
                                borderWidth={3}
                                blur={4}
                            />
                            <Maximize2
                                aria-hidden="true"
                                className="pointer-events-none absolute right-5 top-5 z-20 h-4 w-4 text-label-tertiary opacity-60 transition-all duration-200 group-hover:opacity-100 group-hover:text-accent-text"
                            />
                            <CardContent className="p-6 flex flex-col h-full relative z-10">
                                <h3 className="text-xl font-semibold tracking-tight mb-1 pr-8 text-label">
                                    {exp.title[lang]}
                                </h3>
                                <h4 className="text-[15px] font-semibold mb-2 text-accent-text">
                                    {exp.company}
                                </h4>
                                <p className="mb-3 text-label-secondary text-[15px] leading-relaxed">
                                    {exp.description[lang]}
                                </p>
                                <p className="mb-4 text-label-tertiary text-[13px] flex-1">
                                    {tr.clickDetails}
                                </p>
                                <span className="text-[13px] font-medium text-label-tertiary">
                                    {exp.link === "Private Repository" ? tr.privateRepo : exp.link}
                                </span>
                            </CardContent>
                        </Card>
                    ))}
                </div>
                {/* Fullscreen Modal for Professional Experience */}
                <Modal
                    open={typeof openProject === "number"}
                    onClose={() => setOpenProject(null)}
                    className="max-w-2xl p-8"
                    labelledBy="experience-modal-title"
                >
                    {typeof openProject === "number" && (
                        <>
                            <button
                                className="absolute top-3 right-3 h-11 w-11 rounded-full flex items-center justify-center text-2xl text-label-tertiary hover:text-label hover:bg-black/[0.05] dark:hover:bg-white/[0.08] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                onClick={() => setOpenProject(null)}
                                aria-label="Close"
                            >
                                &times;
                            </button>
                            <h2 id="experience-modal-title" className="text-2xl sm:text-3xl font-bold tracking-tight mb-1 text-label pr-8">
                                {professionalExperience[openProject].title[lang]}
                            </h2>
                            <h4 className="text-[15px] font-semibold mb-4 text-accent-text">
                                {professionalExperience[openProject].company}
                            </h4>
                            <p className="mb-4 text-label text-[17px] leading-relaxed">
                                {professionalExperience[openProject].description[lang]}
                            </p>
                            <div className="mb-6 text-label-secondary text-[15px] whitespace-pre-line leading-relaxed">
                                {professionalExperience[openProject].details[lang]}
                            </div>
                            <span className="text-[13px] font-medium text-label-tertiary">
                                {professionalExperience[openProject].link === "Private Repository"
                                    ? tr.privateRepo
                                    : professionalExperience[openProject].link}
                            </span>
                        </>
                    )}
                </Modal>
            </div>
        </section>
    );
}
